import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import HeroCinematic from "../components/HeroCinematic";
import ManifestoChapters from "../components/ManifestoChapters";
import CollectionShowcase from "../components/CollectionShowcase";
import EditorialSpread from "../components/EditorialSpread";
import Marquee from "../components/Marquee";
import ScrollReveal from "../components/ScrollReveal";
import TonalPanel from "../components/TonalPanel";
import { api } from "../lib/api";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [journal, setJournal] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/journal").then((r) => setJournal(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Loader-into-hero: fixed masked slabs slide up */}
      <Loader />

      <HeroCinematic />

      <Marquee items={["Noir", "Bone", "Taupe", "Plum", "Nude", "korv."]} />

      <ManifestoChapters />

      {products.length > 0 && <CollectionShowcase products={products} />}

      <EditorialSpread />

      {/* Founder's note — asymmetric editorial block */}
      <FoundersNote />

      {/* Journal preview */}
      {journal.length > 0 && <JournalStrip articles={journal.slice(0, 3)} />}

      <CtaEnd />
    </div>
  );
}

function Loader() {
  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 0 }}
      transition={{ duration: 0.6, delay: 2.2 }}
      style={{ pointerEvents: "none" }}
      className="fixed inset-0 z-[60]"
    >
      <div className="absolute inset-0 flex">
        {[0,1,2,3,4].map((i) => (
          <motion.div
            key={i}
            initial={{ y: 0 }}
            animate={{ y: "-101%" }}
            transition={{ duration: 1.3, delay: 1.0 + i * 0.08, ease: [0.85, 0, 0.15, 1] }}
            className="flex-1 bg-ink"
          />
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: [0, 1, 1, 0], y: 0 }}
          transition={{ duration: 1.6, times: [0, 0.2, 0.75, 1] }}
          className="text-bone font-serif-display text-6xl md:text-8xl"
        >
          korv<span className="text-plum">.</span>
        </motion.div>
      </div>
    </motion.div>
  );
}

function FoundersNote() {
  return (
    <section className="py-32 md:py-44">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
        <ScrollReveal className="col-span-12 md:col-span-4">
          <TonalPanel
            bg="taupe"
            tubeColor="#141212"
            cap="dark"
            sublabel="LIP BALM"
            fig="Fig. Studio"
            caption="Noir, on natural taupe."
            chapter="The Studio"
            ratio="aspect-[4/5]"
          />
          <p className="mt-4 text-[10px] uppercase tracking-editorial text-ink/60">The Studio · Redchurch St.</p>
        </ScrollReveal>
        <ScrollReveal delay={0.1} className="col-span-12 md:col-span-7 md:col-start-6">
          <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-8">A note from the house</p>
          <blockquote className="font-serif-display text-4xl md:text-6xl leading-[1.02] max-w-[24ch]">
            "We are not building a beauty brand. We are building a <span className="italic font-serif-italic">house</span> — for the long form."
          </blockquote>
          <div className="mt-10 flex items-center gap-4 text-[11px] uppercase tracking-editorial text-ink/70">
            <span className="w-10 h-[1px] bg-ink/60" />
            The Founder · MMXXV
          </div>
          <p className="mt-14 text-[16px] leading-[1.6] text-ink/80 max-w-[52ch]">
            The first volume is the lip. What follows is already in the studio — a fragrance, a hand cream,
            a body oil, and, in time, a candle for the home. Each will feel like a chapter of the same book,
            in the same handwriting. There is no hurry.
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}

function JournalStrip({ articles }) {
  const covers = [
    { bg: "plum", tube: "#e6ddcf", cap: "light", sub: "LIP GLOSS" },
    { bg: "stone", tube: "#3a2530", cap: "same", sub: "LIP GLOSS" },
    { bg: "charcoal", tube: "#c9a37a", cap: "dark", sub: "LIP GLOSS" },
  ];
  return (
    <section className="border-t border-ink/15 py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-6">Journal</p>
            <h3 className="font-serif-display text-5xl md:text-7xl leading-[0.95] max-w-[14ch]">
              Writing from the <span className="italic font-serif-italic">house</span>.
            </h3>
          </div>
          <Link to="/journal" className="editorial-link text-[11px] uppercase tracking-editorial hidden md:inline-flex">All entries →</Link>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {articles.map((a, i) => {
            const c = covers[i % covers.length];
            return (
              <ScrollReveal key={a.slug} delay={i * 0.05} className={i === 0 ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-3"}>
                <Link to={`/journal/${a.slug}`} className="group block">
                  <TonalPanel
                    bg={c.bg}
                    tubeColor={c.tube}
                    cap={c.cap}
                    sublabel={c.sub}
                    fig={a.chapter}
                    caption={a.title}
                    chapter={a.published}
                    ratio={i === 0 ? "aspect-[4/3]" : "aspect-[3/4]"}
                  />
                  <p className="mt-6 text-[10px] uppercase tracking-editorial text-ink/60">{a.chapter} · {a.read_time} · {a.published}</p>
                  <h4 className="font-serif-display text-2xl md:text-3xl mt-3 max-w-[22ch]">{a.title}</h4>
                  <p className="mt-2 text-[14px] text-ink/70 max-w-[40ch]">{a.subtitle}</p>
                </Link>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaEnd() {
  return (
    <section className="py-32 md:py-56 bg-plum text-bone">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <p className="text-[10px] uppercase tracking-eyebrow text-bone/60 mb-8">Fin — Volume I</p>
        <h3 className="font-serif-display text-5xl md:text-8xl leading-[0.95] max-w-[16ch]">
          Discover the collection, quietly.
        </h3>
        <div className="mt-14 flex flex-col md:flex-row items-start md:items-end gap-6 md:gap-16">
          <Link to="/collection" className="editorial-link text-[11px] uppercase tracking-editorial !text-bone">
            The Collection →
          </Link>
          <Link to="/house" className="editorial-link text-[11px] uppercase tracking-editorial !text-bone/80">
            The House →
          </Link>
          <Link to="/contact" className="editorial-link text-[11px] uppercase tracking-editorial !text-bone/80">
            Book a Private Appointment →
          </Link>
        </div>
      </div>
    </section>
  );
}
